import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { PhaseDefs } from '../../constants/definitions';

const styles = StyleSheet.create({
  container: {
    marginBottom: 30,
  },
  phaseHeader: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 8,
    color: '#333333',
  },
  phaseDescription: {
    fontSize: 8,
    marginBottom: 15,
    lineHeight: 1.4,
    color: '#666666',
  },
  table: {
    width: '100%',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    paddingVertical: 8,
    paddingHorizontal: 6,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    minHeight: 30,
  },
  headerCell: {
    fontSize: 8,
    fontWeight: 600,
    color: '#444444',
    padding: 6,
  },
  cell: {
    fontSize: 7,
    color: '#333333',
  },
  columnContent: {
    padding: 4,
    paddingTop: 6,
    paddingBottom: 6,
  },
  competencyItem: {
    marginBottom: 4,
    paddingBottom: 4,
    padding: 2,
    borderBottomWidth: 0.5,
    borderBottomColor: '#e0e0e0',
    borderBottomStyle: 'dashed',
    paddingHorizontal: 6,
  },
  firstCompetencyItem: {
    paddingTop: 8,
  },
  lastCompetencyItem: {
    marginBottom: 0,
    paddingBottom: 0,
    borderBottomWidth: 0,
  },
  session1Column: {
    width: '33.33%',
    backgroundColor: '#F8F9FA',
  },
  session2Column: {
    width: '33.33%',
    backgroundColor: '#F8F9FA',
  },
  sharedColumn: {
    width: '33.33%',
    backgroundColor: '#E8F5E9',
  },
  headerCellSession1: {
    backgroundColor: '#F8F9FA',
  },
  headerCellSession2: {
    backgroundColor: '#F8F9FA',
  },
  headerCellShared: {
    backgroundColor: '#E8F5E9',
  },
  emptyCell: {
    textAlign: 'center',
    color: '#999999',
    fontSize: 7,
    padding: 8,
  }
});

const ComparisonCompetencyTablePDF = ({ session1Competencies = [], session2Competencies = [], phase }) => {
  const PHASES = ['Empathize', 'Define', 'Ideate', 'Prototype', 'Test', 'Communicate'];

  const phaseData = useMemo(() => {
    const data = {};
    PHASES.forEach(p => {
      data[p] = {
        session1Unique: [],
        session2Unique: [],
        shared: []
      };
    });

    const session1Ids = new Set(session1Competencies.map(comp => comp.id));
    const session2Ids = new Set(session2Competencies.map(comp => comp.id));

    session1Competencies.forEach(competency => {
      const compPhase = competency.abilities?.phase || 'Unknown';
      if (PHASES.includes(compPhase)) {
        if (session2Ids.has(competency.id)) {
          data[compPhase].shared.push(competency);
        } else {
          data[compPhase].session1Unique.push(competency);
        }
      }
    });

    session2Competencies.forEach(competency => {
      const compPhase = competency.abilities?.phase || 'Unknown';
      if (PHASES.includes(compPhase)) {
        if (!session1Ids.has(competency.id)) {
          data[compPhase].session2Unique.push(competency);
        }
      }
    });
    return data;
  }, [session1Competencies, session2Competencies]);

  const getMaxRows = (currentPhase) => {
    const session1Count = phaseData[currentPhase].session1Unique.length;
    const session2Count = phaseData[currentPhase].session2Unique.length;
    const sharedCount = phaseData[currentPhase].shared.length;
    return Math.max(session1Count, session2Count, sharedCount, 1);
  };

  const getCategoryIndicator = (category) => {
    switch (category) {
      case 'skills': return '(S)';
      case 'knowledge': return '(K)';
      case 'mindset': return '(M)';
      case 'tool': return '(I)';
      default: return '';
    }
  };

  if (!phase || !PHASES.includes(phase)) {
    return <View><Text>Invalid phase specified</Text></View>;
  }

  const maxRows = getMaxRows(phase);
  const session1Unique = phaseData[phase].session1Unique;
  const session2Unique = phaseData[phase].session2Unique;
  const shared = phaseData[phase].shared;

  return (
    <View style={styles.container}>
      <Text style={styles.phaseHeader}>{phase}</Text>
      <Text style={styles.phaseDescription}>{PhaseDefs[phase.toUpperCase()]}</Text>
      
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <Text style={[styles.headerCell, styles.headerCellSession1, styles.session1Column]}>
            Session 1 Unique ({session1Unique.length})
          </Text>
          <Text style={[styles.headerCell, styles.headerCellSession2, styles.session2Column]}>
            Session 2 Unique ({session2Unique.length})
          </Text>
          <Text style={[styles.headerCell, styles.headerCellShared, styles.sharedColumn]}>
            Shared ({shared.length})
          </Text>
        </View>

        {Array.from({ length: maxRows }, (_, rowIndex) => (
          <View key={rowIndex} style={styles.tableRow}>
            <View style={styles.session1Column}>
              {rowIndex < session1Unique.length ? (
                <View style={[styles.cell, styles.columnContent]}>
                  <View style={[
                    styles.competencyItem,
                    rowIndex === 0 && styles.firstCompetencyItem,
                    rowIndex === session1Unique.length - 1 && styles.lastCompetencyItem
                  ]}>
                    <Text style={styles.cell}>
                      {session1Unique[rowIndex].name} {getCategoryIndicator(session1Unique[rowIndex].category)}
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={[styles.cell, styles.columnContent]}>
                  <Text style={styles.emptyCell}></Text>
                </View>
              )}
            </View>
            
            <View style={styles.session2Column}>
              {rowIndex < session2Unique.length ? (
                <View style={[styles.cell, styles.columnContent]}>
                  <View style={[
                    styles.competencyItem,
                    rowIndex === 0 && styles.firstCompetencyItem,
                    rowIndex === session2Unique.length - 1 && styles.lastCompetencyItem
                  ]}>
                    <Text style={styles.cell}>
                      {session2Unique[rowIndex].name} {getCategoryIndicator(session2Unique[rowIndex].category)}
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={[styles.cell, styles.columnContent]}>
                  <Text style={styles.emptyCell}></Text>
                </View>
              )}
            </View>

            <View style={styles.sharedColumn}>
              {rowIndex < shared.length ? (
                <View style={[styles.cell, styles.columnContent]}>
                  <View style={[
                    styles.competencyItem,
                    rowIndex === 0 && styles.firstCompetencyItem,
                    rowIndex === shared.length - 1 && styles.lastCompetencyItem
                  ]}>
                    <Text style={styles.cell}>
                      {shared[rowIndex].name} {getCategoryIndicator(shared[rowIndex].category)}
                    </Text>
                  </View>
                </View>
              ) : (
                <View style={[styles.cell, styles.columnContent]}>
                  <Text style={styles.emptyCell}></Text>
                </View>
              )}
            </View>
          </View>
        ))}
      </View>
    </View>
  );
};

export default ComparisonCompetencyTablePDF;
